/**
 * OpenRouter Data Source - v13 Section 6.10 (Sprint 11g)
 *
 * Provides two key functions for the Rosetta Stone pattern:
 * 1. fetchFromOpenRouter() - Full config fetch (existing)
 * 2. fetchMetadataMap() - Normalized ID → metadata lookup (NEW)
 *
 * The metadata map is used to enrich provider API model IDs with:
 * - pricing (inputPer1M, outputPer1M)
 * - contextWindow
 * - maxCompletionTokens
 * - displayName
 *
 * @see https://openrouter.ai/docs/api/api-reference/models/get-models
 */
import { OPENROUTER_URL } from '../defaults';
/**
 * Extract provider from OpenRouter model ID (format: "provider/model")
 */
const extractProvider = (modelId) => {
    const prefix = modelId.split('/')[0]?.toLowerCase();
    const prefixToProvider = {
        openai: 'openai',
        anthropic: 'anthropic',
        google: 'google',
        'meta-llama': 'deepinfra',
        mistralai: 'deepinfra',
        qwen: 'deepinfra',
        deepseek: 'deepinfra',
        groq: 'groq',
    };
    return prefixToProvider[prefix] ?? null;
};
/**
 * Extract model name from OpenRouter ID
 */
const extractModelName = (modelId) => {
    // OpenRouter format: "provider/model-name"
    const parts = modelId.split('/');
    return parts[parts.length - 1] ?? modelId;
};
/**
 * Get context window from API response
 * Returns null if not provided by API
 */
const getContextWindow = (contextLength) => {
    if (contextLength && contextLength > 0) {
        return contextLength;
    }
    return null;
};
/**
 * Get max completion tokens from API response
 * Returns null if not provided by API
 */
const getMaxCompletionTokens = (model) => {
    return model.top_provider?.max_completion_tokens ?? null;
};
/**
 * Determine if model is a reasoning model from API response
 * Uses multiple signals:
 * 1. architecture.instruct_type === 'reasoning'
 * 2. supported_parameters includes 'reasoning' or 'include_reasoning'
 */
const isReasoningModel = (model) => {
    // Check instruct_type first
    if (model.architecture?.instruct_type === 'reasoning') {
        return true;
    }
    // Check supported_parameters for reasoning capability
    const params = model.supported_parameters ?? [];
    if (params.includes('reasoning') || params.includes('include_reasoning')) {
        return true;
    }
    // If instruct_type is explicitly set to something else, it's not a reasoning model
    if (model.architecture?.instruct_type !== undefined) {
        return false;
    }
    // No conclusive data - return null (unknown)
    return null;
};
/**
 * Determine if provider has zero data retention
 * Returns null if unknown (OpenRouter doesn't provide this data)
 */
const isZeroDataRetention = (provider) => {
    // Known ZDR providers - this is structural knowledge, not model data
    if (provider === 'groq' || provider === 'deepinfra' || provider === 'ollama') {
        return true;
    }
    // For other providers, we don't know from OpenRouter data
    return null;
};
/**
 * Parse pricing string to number (OpenRouter uses strings)
 */
const parsePricing = (priceStr) => {
    const price = parseFloat(priceStr);
    if (isNaN(price))
        return 0;
    // OpenRouter pricing is per token, convert to per 1M tokens
    return price * 1000000;
};
/**
 * Empty tier configuration - tiers should be configured at application level
 */
const EMPTY_TIERS = {
    fast: {
        primaryModel: '',
        fallbackModels: [],
        avgCostPer1k: 0,
        description: 'Fast models - configure at application level',
    },
    standard: {
        primaryModel: '',
        fallbackModels: [],
        avgCostPer1k: 0,
        description: 'Standard models - configure at application level',
    },
    quality: {
        primaryModel: '',
        fallbackModels: [],
        avgCostPer1k: 0,
        description: 'Quality models - configure at application level',
    },
    local: {
        primaryModel: '',
        fallbackModels: [],
        avgCostPer1k: 0,
        description: 'Local models - configure at application level',
    },
};
/**
 * Transform OpenRouter response to our config format
 * No hardcoded model data - uses only API-provided values
 */
const transformOpenRouterResponse = (response) => {
    const models = {};
    const fallbackModels = [];
    const providerModels = {};
    for (const model of response.data) {
        const provider = extractProvider(model.id);
        if (!provider)
            continue; // Skip unknown providers
        // Use short model name as key (e.g., "gpt-4o-mini" not "openai/gpt-4o-mini")
        const shortId = extractModelName(model.id);
        // Parse pricing - may be null if not provided
        const inputPrice = parsePricing(model.pricing?.prompt ?? '0');
        const outputPrice = parsePricing(model.pricing?.completion ?? '0');
        const hasPricing = inputPrice > 0 || outputPrice > 0;
        models[shortId] = {
            id: shortId,
            provider,
            displayName: model.name || shortId,
            contextWindow: getContextWindow(model.context_length),
            maxCompletionTokens: getMaxCompletionTokens(model),
            pricing: hasPricing ? {
                inputPer1M: inputPrice,
                outputPer1M: outputPrice,
            } : null,
            capabilities: [],
            zeroDataRetention: isZeroDataRetention(provider),
            isReasoningModel: isReasoningModel(model),
        };
        // Track for fallback selection
        if (!providerModels[provider]) {
            providerModels[provider] = [];
        }
        providerModels[provider].push(shortId);
    }
    // Pick first model from each provider for fallbacks
    for (const [provider, modelIds] of Object.entries(providerModels)) {
        if (modelIds.length > 0) {
            const modelId = modelIds[0];
            const metadata = models[modelId];
            if (metadata) {
                fallbackModels.push({
                    provider,
                    model: modelId,
                    displayName: metadata.displayName,
                });
            }
        }
    }
    return {
        timestamp: Date.now(),
        source: 'openrouter',
        models,
        tiers: EMPTY_TIERS,
        fallbackModels,
    };
};
/**
 * Fetch configuration from OpenRouter
 *
 * @throws Error if fetch fails
 */
export const fetchFromOpenRouter = async () => {
    const response = await fetch(OPENROUTER_URL, {
        headers: {
            Accept: 'application/json',
        },
    });
    if (!response.ok) {
        throw new Error(`OpenRouter returned ${response.status}: ${response.statusText}`);
    }
    const data = await response.json();
    if (!data.data || !Array.isArray(data.data)) {
        throw new Error('Invalid response format from OpenRouter');
    }
    return transformOpenRouterResponse(data);
};
/**
 * Normalize a model ID for lookup in the metadata map
 *
 * The Rosetta Stone pattern requires matching provider API model IDs
 * (e.g., "claude-3-5-haiku-20241022") to OpenRouter metadata.
 *
 * OpenRouter uses "provider/model" format, so we:
 * 1. Strip provider prefix if present
 * 2. Lowercase for case-insensitive matching
 * 3. Remove version/date suffixes for fuzzy matching
 *
 * Example normalizations:
 *   "anthropic/claude-3-5-haiku-20241022" → "claude-3-5-haiku"
 *   "openai/gpt-5-nano-2025-08-07" → "gpt-5-nano"
 *   "gpt-4o-2024-08-06" → "gpt-4o"
 */
export const normalizeModelId = (modelId) => {
    // Step 1: Strip provider prefix if present (OpenRouter format: "provider/model")
    const parts = modelId.split('/');
    let shortId = parts[parts.length - 1];
    // Step 2: Lowercase for case-insensitive matching
    shortId = shortId.toLowerCase();
    // Step 3: Remove date suffixes for fuzzy matching
    shortId = shortId.replace(/-\d{4}-\d{2}-\d{2}$/, ''); // ISO date: -2025-08-07
    shortId = shortId.replace(/-\d{8}$/, ''); // Compact date: -20241022
    return shortId;
};
/**
 * Fetch metadata map from OpenRouter for the Rosetta Stone pattern
 *
 * This builds a lookup table of normalized model IDs to their metadata.
 * Use this to enrich provider API model IDs with:
 * - pricing
 * - contextWindow
 * - maxCompletionTokens
 * - displayName
 *
 * @returns MetadataMap - Map<normalizedId, MetadataMapEntry>
 * @throws Error if fetch fails
 */
export const fetchMetadataMap = async () => {
    const response = await fetch(OPENROUTER_URL, {
        headers: {
            Accept: 'application/json',
        },
    });
    if (!response.ok) {
        throw new Error(`OpenRouter returned ${response.status}: ${response.statusText}`);
    }
    const data = await response.json();
    if (!data.data || !Array.isArray(data.data)) {
        throw new Error('Invalid response format from OpenRouter');
    }
    const metadataMap = new Map();
    for (const model of data.data) {
        // Normalize the ID for lookup
        const normalizedId = normalizeModelId(model.id);
        // Also store the short ID (without provider prefix)
        const shortId = extractModelName(model.id);
        // Parse pricing
        const inputPrice = parsePricing(model.pricing?.prompt ?? '0');
        const outputPrice = parsePricing(model.pricing?.completion ?? '0');
        const hasPricing = inputPrice > 0 || outputPrice > 0;
        const entry = {
            displayName: model.name || shortId,
            contextWindow: getContextWindow(model.context_length),
            maxCompletionTokens: getMaxCompletionTokens(model),
            pricing: hasPricing ? {
                inputPer1M: inputPrice,
                outputPer1M: outputPrice,
            } : null,
            isReasoningModel: isReasoningModel(model),
        };
        // Store under normalized ID (lowercase)
        metadataMap.set(normalizedId, entry);
        // Also store under exact short ID (original case) for exact matches
        if (shortId !== normalizedId) {
            metadataMap.set(shortId, entry);
        }
    }
    return metadataMap;
};
